
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';

interface Category {
  id: string;
  name: string;
  parent_id: string | null;
}

interface BasicInfoFormProps {
  template: any;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (name: string, value: string) => void;
}

export function BasicInfoForm({ template, onInputChange, onSelectChange }: BasicInfoFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  
  useEffect(() => {
    fetchCategories();
  }, []);
  
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('email_template_categories')
        .select('id, name, parent_id')
        .order('display_order');
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Function to get the parent category name
  const getParentCategoryName = (parentId: string | null) => {
    if (!parentId) return null;
    const parent = categories.find(c => c.id === parentId);
    return parent ? parent.name : null;
  };
  
  // Function to format a category name with its parent
  const formatCategoryName = (category: Category) => {
    const parentName = getParentCategoryName(category.parent_id);
    return parentName ? `${parentName} / ${category.name}` : category.name;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                name="name"
                value={template.name}
                onChange={onInputChange}
                placeholder="Welcome Email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template_id">Template ID</Label>
              <Input
                id="template_id"
                name="template_id"
                value={template.template_id}
                onChange={onInputChange}
                placeholder="welcome_email"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier used in code
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Email Subject</Label>
            <Input
              id="subject"
              name="subject"
              value={template.subject}
              onChange={onInputChange}
              placeholder="Welcome to DriveCourses!"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={template.description || ''}
              onChange={onInputChange}
              placeholder="This email is sent when a new user signs up."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                name="category_id"
                value={template.category_id || ''}
                onChange={(e) => onSelectChange('category_id', e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Select a category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {formatCategoryName(category)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-legacy">Category (Legacy)</Label>
              <Input
                id="category-legacy"
                name="category"
                value={template.category}
                onChange={onInputChange}
                placeholder="onboarding"
              />
              <p className="text-xs text-muted-foreground">
                Legacy category field (use dropdown above instead)
              </p>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
